import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaExclamationTriangle, FaPlus, FaTrash } from 'react-icons/fa';
import * as Sentry from "@sentry/react";

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'firewall-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'firewall-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'firewall-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const sanitizeNetworkInput = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[^0-9a-fA-F:.\/\-]/g, '')
    .substring(0, 50);
};

const sanitizePort = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/[^0-9]/g, '').substring(0, 5);
};

// ============================================================================
// RESPONSIVE HOOK
// ============================================================================
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return { isMobile, isTablet };
};

// ============================================================================
// SHIELD ICON COMPONENT
// ============================================================================
const ShieldIcon = ({ size = 20, color = '#1a1a1a' }) => (
  <svg width={size} height={size + 4} viewBox="0 0 24 28" fill={color}>
    <path d="M12 0L0 4v10c0 6 5 12 12 14 7-2 12-8 12-14V4L12 0z"/>
  </svg>
);

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isMobile, isTablet) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: isMobile ? '12px' : '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: isMobile ? '100%' : isTablet ? '600px' : '720px',
    maxHeight: isMobile ? '95vh' : '90vh',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: isMobile ? '20px' : '24px',
    borderRight: isMobile ? 'none' : '1px solid #2a2a2a',
    borderBottom: isMobile ? '1px solid #2a2a2a' : 'none',
    display: 'flex',
    flexDirection: 'column',
    overflowY: isMobile ? 'auto' : 'visible',
  },
  rightPanel: {
    width: isMobile ? '100%' : '260px',
    padding: isMobile ? '20px' : '20px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: isMobile ? '300px' : 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  iconWrapper: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    boxShadow: '0 4px 12px rgba(224,234,204,0.3)',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: isMobile ? '17px' : '18px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '12px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  label: {
    color: '#9ca3af',
    fontSize: '11px',
    fontWeight: '500',
    marginBottom: '5px',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: isMobile ? '11px 12px' : '9px 11px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: 'white',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdown: {
    position: 'relative',
  },
  dropdownTrigger: {
    width: '100%',
    padding: isMobile ? '11px 12px' : '9px 11px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: 'white',
    fontSize: '13px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    zIndex: 100,
    overflow: 'hidden',
  },
  dropdownItem: {
    padding: isMobile ? '11px 12px' : '9px 11px',
    color: 'white',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: isMobile ? '14px' : '12px',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '10px 12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  addBtn: (disabled) => ({
    width: '100%',
    padding: isMobile ? '13px' : '11px',
    backgroundColor: disabled ? '#3d4435' : '#e0eacc',
    color: disabled ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '16px',
    boxShadow: disabled ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    transition: 'all 0.15s',
  }),
  rulesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    flexShrink: 0,
  },
  rulesTitle: {
    color: '#6b7280',
    fontSize: '11px',
    letterSpacing: '1.5px',
    margin: 0,
  },
  rulesBadge: (count) => ({
    padding: '3px 8px',
    backgroundColor: count > 10 ? 'rgba(234,179,8,0.15)' : 'rgba(224,234,204,0.15)',
    color: count > 10 ? '#fcd34d' : '#e0eacc',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: '600',
  }),
  statsRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
    flexShrink: 0,
  },
  statBadge: {
    flex: 1,
    padding: '8px',
    backgroundColor: '#1a1a1a',
    borderRadius: '6px',
    textAlign: 'center',
    border: '1px solid #333',
  },
  statValue: (type) => ({
    color: type === 'ingress' ? '#60a5fa' : '#fcd34d',
    fontSize: '16px',
    fontWeight: '700',
    margin: 0,
  }),
  statLabel: {
    color: '#6b7280',
    fontSize: '9px',
    textTransform: 'uppercase',
    margin: '2px 0 0 0',
  },
  rulesList: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    paddingRight: '4px',
    maxHeight: isMobile ? '140px' : '168px',
  },
  ruleItem: {
    padding: '8px 10px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  ruleBadge: (type) => ({
    padding: '2px 5px',
    borderRadius: '4px',
    fontSize: '9px',
    fontWeight: '700',
    backgroundColor: type === 'ingress' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(234, 179, 8, 0.15)',
    color: type === 'ingress' ? '#60a5fa' : '#fcd34d',
  }),
  deleteBtn: {
    padding: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7,
    transition: 'all 0.15s',
  },
  emptyRules: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
    textAlign: 'center',
    padding: '30px 20px',
  },
  emptyTitle: {
    margin: '12px 0 0 0',
    fontSize: '13px',
    color: '#555',
  },
  emptyText: {
    margin: '4px 0 0 0',
    fontSize: '11px',
    color: '#3a3a3a',
  },
  scrollHint: {
    textAlign: 'center',
    padding: '8px 0 0 0',
    color: '#555',
    fontSize: '10px',
    flexShrink: 0,
  },
  actionBtns: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #222',
    flexShrink: 0,
  },
  saveBtn: (disabled) => ({
    width: '100%',
    padding: isMobile ? '12px' : '10px',
    backgroundColor: disabled ? '#3d4435' : '#e0eacc',
    color: disabled ? '#7a8a6a' : 'black',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    marginBottom: '6px',
    transition: 'all 0.15s',
  }),
  cancelBtn: {
    width: '100%',
    padding: isMobile ? '12px' : '10px',
    backgroundColor: '#333',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const FirewallModal = ({ isOpen, onClose, onSave, initialRules = [] }) => {
  const { isMobile, isTablet } = useResponsive();
  const styles = getStyles(isMobile, isTablet);
  
  // State
  const [rules, setRules] = useState(initialRules);
  const [currentRule, setCurrentRule] = useState({
    direction: 'ingress',
    protocol: 'tcp',
    ethertype: 'IPv4',
    remote_ip_prefix: '0.0.0.0/0',
    port_range_min: '',
    port_range_max: ''
  });
  const [error, setError] = useState(null);
  
  // Dropdown states
  const [isDirectionDropdownOpen, setIsDirectionDropdownOpen] = useState(false);
  const [isProtocolDropdownOpen, setIsProtocolDropdownOpen] = useState(false);
  const [isEthertypeDropdownOpen, setIsEthertypeDropdownOpen] = useState(false);
  
  // Refs
  const directionRef = useRef(null);
  const protocolRef = useRef(null);
  const ethertypeRef = useRef(null);
  
  // Derived values
  const ingressCount = rules.filter(r => r.direction === 'ingress').length;
  const egressCount = rules.filter(r => r.direction === 'egress').length;
  const showPorts = currentRule.protocol === 'tcp' || currentRule.protocol === 'udp';

  // ============================================================================
  // SENTRY CONTEXT
  // ============================================================================
  useEffect(() => {
    if (isOpen) {
      Sentry.setContext("firewall-modal", {
        isOpen,
        initialRulesCount: initialRules.length,
        currentRulesCount: rules.length,
        isMobile,
        isTablet
      });
      
      logInfo('Firewall modal opened', {
        initialRulesCount: initialRules.length,
        currentRulesCount: rules.length,
        isMobile,
        isTablet
      });
      
      logUserAction('firewall_modal_opened', {
        initialRulesCount: initialRules.length,
        isMobile,
        isTablet
      });
    }
  }, [isOpen, initialRules.length, rules.length, isMobile, isTablet]);

  // ============================================================================
  // RESET RULES ON MODAL OPEN
  // ============================================================================
  useEffect(() => {
    if (isOpen) {
      setRules(initialRules);
      setError(null);
      setCurrentRule({
        direction: 'ingress',
        protocol: 'tcp',
        ethertype: 'IPv4',
        remote_ip_prefix: '0.0.0.0/0',
        port_range_min: '',
        port_range_max: ''
      });
      logInfo('Modal rules reset to initial state', { rulesCount: initialRules.length });
    }
  }, [isOpen, initialRules]);

  // ============================================================================
  // CLOSE DROPDOWNS ON OUTSIDE CLICK
  // ============================================================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (directionRef.current && !directionRef.current.contains(event.target)) {
        setIsDirectionDropdownOpen(false);
      }
      if (protocolRef.current && !protocolRef.current.contains(event.target)) {
        setIsProtocolDropdownOpen(false);
      }
      if (ethertypeRef.current && !ethertypeRef.current.contains(event.target)) {
        setIsEthertypeDropdownOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // ============================================================================
  // DROPDOWN HANDLERS
  // ============================================================================
  const closeAllDropdowns = useCallback(() => {
    setIsDirectionDropdownOpen(false);
    setIsProtocolDropdownOpen(false);
    setIsEthertypeDropdownOpen(false);
  }, []);

  const toggleDirectionDropdown = useCallback((e) => {
    e.stopPropagation();
    const newState = !isDirectionDropdownOpen;
    setIsDirectionDropdownOpen(newState);
    setIsProtocolDropdownOpen(false);
    setIsEthertypeDropdownOpen(false);
    logUserAction('direction_dropdown_toggled', { opened: newState, currentValue: currentRule.direction });
  }, [isDirectionDropdownOpen, currentRule.direction]);

  const toggleProtocolDropdown = useCallback((e) => {
    e.stopPropagation();
    const newState = !isProtocolDropdownOpen;
    setIsProtocolDropdownOpen(newState);
    setIsDirectionDropdownOpen(false);
    setIsEthertypeDropdownOpen(false);
    logUserAction('protocol_dropdown_toggled', { opened: newState, currentValue: currentRule.protocol });
  }, [isProtocolDropdownOpen, currentRule.protocol]);

  const toggleEthertypeDropdown = useCallback((e) => {
    e.stopPropagation();
    const newState = !isEthertypeDropdownOpen;
    setIsEthertypeDropdownOpen(newState);
    setIsDirectionDropdownOpen(false);
    setIsProtocolDropdownOpen(false);
    logUserAction('ethertype_dropdown_toggled', { opened: newState, currentValue: currentRule.ethertype });
  }, [isEthertypeDropdownOpen, currentRule.ethertype]);

  const handleDirectionSelect = useCallback((value) => {
    const previousValue = currentRule.direction;
    setCurrentRule(prev => ({ ...prev, direction: value }));
    setIsDirectionDropdownOpen(false);
    logUserAction('direction_selected', { previousValue, newValue: value });
    logInfo('Direction changed', { from: previousValue, to: value });
  }, [currentRule.direction]);

  const handleProtocolSelect = useCallback((value) => {
    const previousValue = currentRule.protocol;
    setCurrentRule(prev => ({
      ...prev,
      protocol: value,
      port_range_min: value !== 'tcp' && value !== 'udp' ? '' : prev.port_range_min,
      port_range_max: value !== 'tcp' && value !== 'udp' ? '' : prev.port_range_max
    }));
    setIsProtocolDropdownOpen(false);
    logUserAction('protocol_selected', { previousValue, newValue: value, willResetPorts: value !== 'tcp' && value !== 'udp' });
    logInfo('Protocol changed', { from: previousValue, to: value, portsWillBeReset: value !== 'tcp' && value !== 'udp' });
  }, [currentRule.protocol]);

  const handleEthertypeSelect = useCallback((value) => {
    const previousValue = currentRule.ethertype;
    setCurrentRule(prev => ({ ...prev, ethertype: value }));
    setIsEthertypeDropdownOpen(false);
    logUserAction('ethertype_selected', { previousValue, newValue: value });
    logInfo('Ethertype changed', { from: previousValue, to: value });
  }, [currentRule.ethertype]);

  // ============================================================================
  // INPUT HANDLERS
  // ============================================================================
  const handleRuleChange = useCallback((field, value) => {
    let sanitizedValue = value;
    if (field === 'remote_ip_prefix') {
      sanitizedValue = sanitizeNetworkInput(value);
    } else if (field === 'port_range_min' || field === 'port_range_max') {
      sanitizedValue = sanitizePort(value);
    }

    setCurrentRule(prev => ({ ...prev, [field]: sanitizedValue }));

    if (field === 'remote_ip_prefix' || field.includes('port')) {
      logUserAction('rule_field_changed', { field, newValue: sanitizedValue, protocol: currentRule.protocol });
    }
  }, [currentRule.protocol]);

  // ============================================================================
  // VALIDATION
  // ============================================================================
  const validateIpPrefix = useCallback((value) => {
    const startTime = performance.now();
    
    if (!value) {
      logError('IP prefix validation failed - empty value', { ethertype: currentRule.ethertype });
      return 'Remote IP prefix is required';
    }
    
    if (currentRule.ethertype === 'IPv4') {
      const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
      if (!ipv4Regex.test(value)) {
        logError('IPv4 CIDR format validation failed', { inputValue: value });
        return 'Invalid IPv4 CIDR format. Example: 192.168.1.0/24';
      }
      
      const parts = value.split('/');
      const ipParts = parts[0].split('.');
      
      for (const part of ipParts) {
        const num = parseInt(part, 10);
        if (num < 0 || num > 255) {
          logError('IPv4 address part out of range', { inputValue: value, invalidPart: part });
          return 'IPv4 address parts must be between 0 and 255';
        }
      }
      
      const prefix = parseInt(parts[1], 10);
      if (isNaN(prefix) || prefix < 0 || prefix > 32) {
        logError('IPv4 CIDR prefix out of range', { inputValue: value, prefix: parts[1] });
        return 'IPv4 CIDR prefix must be between 0 and 32';
      }
    } else if (currentRule.ethertype === 'IPv6') {
      const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}\/\d{1,3}$/;
      if (!ipv6Regex.test(value)) {
        logError('IPv6 CIDR format validation failed', { inputValue: value });
        return 'Invalid IPv6 CIDR format';
      }
      
      const parts = value.split('/');
      const prefix = parseInt(parts[1], 10);
      if (isNaN(prefix) || prefix < 0 || prefix > 128) {
        logError('IPv6 CIDR prefix out of range', { inputValue: value, prefix: parts[1] });
        return 'IPv6 CIDR prefix must be between 0 and 128';
      }
    }
    
    const endTime = performance.now();
    logPerformance('ip_prefix_validation', endTime - startTime, { inputValue: value, ethertype: currentRule.ethertype, success: true });
    
    return '';
  }, [currentRule.ethertype]);

  const validateRule = useCallback(() => {
    const startTime = performance.now();
    
    logInfo('Starting rule validation', {
      direction: currentRule.direction,
      protocol: currentRule.protocol,
      ethertype: currentRule.ethertype,
      hasMinPort: !!currentRule.port_range_min,
      hasMaxPort: !!currentRule.port_range_max
    });
    
    const ipError = validateIpPrefix(currentRule.remote_ip_prefix);
    if (ipError) {
      setError(ipError);
      logError('Rule validation failed at IP prefix', { error: ipError, remoteIpPrefix: currentRule.remote_ip_prefix });
      return false;
    }

    if ((currentRule.protocol === 'tcp' || currentRule.protocol === 'udp')) {
      if ((currentRule.port_range_min && !currentRule.port_range_max) || 
          (!currentRule.port_range_min && currentRule.port_range_max)) {
        const error = 'Both min and max port must be specified';
        setError(error);
        logError('Rule validation failed - incomplete port range', { error, minPort: currentRule.port_range_min, maxPort: currentRule.port_range_max });
        return false;
      }

      if (currentRule.port_range_min && currentRule.port_range_max) {
        const min = parseInt(currentRule.port_range_min);
        const max = parseInt(currentRule.port_range_max);
        
        if (isNaN(min) || isNaN(max)) {
          const error = 'Port values must be numeric';
          setError(error);
          logError('Rule validation failed - non-numeric ports', { error, minPortRaw: currentRule.port_range_min, maxPortRaw: currentRule.port_range_max });
          return false;
        }
        
        if (min < 1 || min > 65535 || max < 1 || max > 65535) {
          const error = 'Port range must be between 1 and 65535';
          setError(error);
          logError('Rule validation failed - port out of range', { error, minPort: min, maxPort: max });
          return false;
        }
        
        if (min > max) {
          const error = 'Min port must be less than or equal to max port';
          setError(error);
          logError('Rule validation failed - min port greater than max', { error, minPort: min, maxPort: max });
          return false;
        }
      }
    }

    const endTime = performance.now();
    logPerformance('rule_validation', endTime - startTime, { success: true, protocol: currentRule.protocol, hasPorts: !!(currentRule.port_range_min && currentRule.port_range_max) });
    logInfo('Rule validation completed successfully', { duration: `${(endTime - startTime).toFixed(2)}ms` });

    return true;
  }, [currentRule, validateIpPrefix]);

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================
  const addRule = useCallback(() => {
    const startTime = performance.now();
    
    logUserAction('add_rule_initiated', {
      direction: currentRule.direction,
      protocol: currentRule.protocol,
      ethertype: currentRule.ethertype,
      hasPortRange: !!(currentRule.port_range_min && currentRule.port_range_max),
      currentRulesCount: rules.length,
      isMobile,
      isTablet
    });
    
    setError(null);
    
    if (!validateRule()) {
      logUserAction('add_rule_failed_validation', { currentRulesCount: rules.length });
      return;
    }
    
    const newRule = { ...currentRule };
    
    if (newRule.port_range_min) {
      newRule.port_range_min = parseInt(newRule.port_range_min);
    }
    if (newRule.port_range_max) {
      newRule.port_range_max = parseInt(newRule.port_range_max);
    }
    
    const updatedRules = [...rules, newRule];
    setRules(updatedRules);
    
    const endTime = performance.now();
    
    logPerformance('add_rule', endTime - startTime, {
      success: true,
      newRulesCount: updatedRules.length,
      ruleAdded: { direction: newRule.direction, protocol: newRule.protocol, ethertype: newRule.ethertype }
    });
    
    logInfo('Rule added successfully', {
      newRulesCount: updatedRules.length,
      addedRule: { direction: newRule.direction, protocol: newRule.protocol, ethertype: newRule.ethertype },
      duration: `${(endTime - startTime).toFixed(2)}ms`
    });
    
    logUserAction('add_rule_success', { newRulesCount: updatedRules.length, ruleDirection: newRule.direction, ruleProtocol: newRule.protocol });
    
    setCurrentRule({
      direction: 'ingress',
      protocol: 'tcp',
      ethertype: 'IPv4',
      remote_ip_prefix: '0.0.0.0/0',
      port_range_min: '',
      port_range_max: ''
    });
    
    logInfo('Rule form reset for next entry');
  }, [currentRule, rules, validateRule, isMobile, isTablet]);

  const removeRule = useCallback((index) => {
    const ruleToRemove = rules[index];
    const newRules = [...rules];
    newRules.splice(index, 1);
    setRules(newRules);
    
    logUserAction('remove_rule', {
      ruleIndex: index,
      removedRule: { direction: ruleToRemove?.direction, protocol: ruleToRemove?.protocol, ethertype: ruleToRemove?.ethertype },
      previousRulesCount: rules.length,
      newRulesCount: newRules.length,
      isMobile,
      isTablet
    });
    
    logInfo('Rule removed', {
      ruleIndex: index,
      newRulesCount: newRules.length,
      removedRule: { direction: ruleToRemove?.direction, protocol: ruleToRemove?.protocol }
    });
  }, [rules, isMobile, isTablet]);

  const handleSave = useCallback(() => {
    logUserAction('save_rules_initiated', {
      rulesCount: rules.length,
      rules: rules.map(rule => ({ direction: rule.direction, protocol: rule.protocol, ethertype: rule.ethertype, hasPortRange: !!(rule.port_range_min && rule.port_range_max) })),
      isMobile,
      isTablet
    });
    
    logInfo('Saving firewall rules', { rulesCount: rules.length });
    
    try {
      onSave(rules);
      logInfo('Rules saved successfully', { rulesCount: rules.length });
      logUserAction('save_rules_success', { rulesCount: rules.length });
      onClose();
      logUserAction('modal_closed_after_save');
    } catch (err) {
      logError('Error saving rules', { rulesCount: rules.length }, err);
      logUserAction('save_rules_error', { rulesCount: rules.length });
    }
  }, [rules, onSave, onClose, isMobile, isTablet]);

  const handleClose = useCallback(() => {
    logUserAction('modal_close_initiated', {
      rulesCount: rules.length,
      hasUnsavedChanges: rules.length !== initialRules.length,
      isMobile,
      isTablet
    });
    
    logInfo('Modal closing', { rulesCount: rules.length, initialRulesCount: initialRules.length });
    closeAllDropdowns();
    onClose();
  }, [rules.length, initialRules.length, onClose, closeAllDropdowns, isMobile, isTablet]);

  // ============================================================================
  // RENDER
  // ============================================================================
  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .fw-scrollbar::-webkit-scrollbar { width: 5px; }
        .fw-scrollbar::-webkit-scrollbar-track { background: #1a1a1a; border-radius: 3px; }
        .fw-scrollbar::-webkit-scrollbar-thumb { background: #444; border-radius: 3px; }
        .fw-scrollbar::-webkit-scrollbar-thumb:hover { background: #555; }
      `}</style>
      
      <div style={styles.overlay}>
        <div style={styles.modal}>
          {/* LEFT PANEL - Add Rule Form */}
          <div style={styles.leftPanel}>
            <div style={styles.header}>
              <div style={styles.headerLeft}>
                <div style={styles.iconWrapper}>
                  <ShieldIcon size={20} color="#1a1a1a" />
                </div>
                <div>
                  <h2 style={styles.title}>Manage Firewall Rules</h2>
                  <p style={styles.subtitle}>Configure inbound and outbound traffic</p>
                </div>
              </div>
              <button
                style={styles.closeBtn}
                onClick={handleClose}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1a1a1a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <FaTimes size={18} />
              </button>
            </div>
            
            {/* Error Display */}
            {error && (
              <div style={styles.errorBox}>
                <FaExclamationTriangle size={14} style={{ color: '#ef4444', marginTop: '1px', flexShrink: 0 }} />
                <span style={{ color: '#fca5a5', fontSize: '12px' }}>{sanitizeText(error)}</span>
              </div>
            )}
            
            {/* Form Grid */}
            <div style={styles.formGrid}>
              {/* Direction */}
              <div style={styles.dropdown} ref={directionRef}>
                <label style={styles.label}>Direction</label>
                <div
                  style={{
                    ...styles.dropdownTrigger,
                    borderColor: isDirectionDropdownOpen ? '#e0eacc' : '#333'
                  }}
                  onClick={toggleDirectionDropdown}
                >
                  <span>{currentRule.direction === 'ingress' ? 'Ingress (Incoming)' : 'Egress (Outgoing)'}</span>
                  <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                </div>
                {isDirectionDropdownOpen && (
                  <div style={styles.dropdownMenu}>
                    {[
                      { value: 'ingress', label: 'Ingress (Incoming)' },
                      { value: 'egress', label: 'Egress (Outgoing)' }
                    ].map(option => (
                      <div
                        key={option.value}
                        style={{
                          ...styles.dropdownItem,
                          backgroundColor: currentRule.direction === option.value ? '#252525' : 'transparent'
                        }}
                        onClick={() => handleDirectionSelect(option.value)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentRule.direction === option.value ? '#252525' : 'transparent'}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Protocol */}
              <div style={styles.dropdown} ref={protocolRef}>
                <label style={styles.label}>Protocol</label>
                <div
                  style={{
                    ...styles.dropdownTrigger,
                    borderColor: isProtocolDropdownOpen ? '#e0eacc' : '#333'
                  }}
                  onClick={toggleProtocolDropdown}
                >
                  <span>{currentRule.protocol.toUpperCase()}</span>
                  <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                </div>
                {isProtocolDropdownOpen && (
                  <div style={styles.dropdownMenu}>
                    {['tcp', 'udp', 'icmp'].map(proto => (
                      <div
                        key={proto}
                        style={{
                          ...styles.dropdownItem,
                          backgroundColor: currentRule.protocol === proto ? '#252525' : 'transparent'
                        }}
                        onClick={() => handleProtocolSelect(proto)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentRule.protocol === proto ? '#252525' : 'transparent'}
                      >
                        {proto.toUpperCase()}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Remote IP */}
              <div>
                <label style={styles.label}>Remote IP Range</label>
                <input
                  type="text"
                  value={currentRule.remote_ip_prefix}
                  onChange={(e) => handleRuleChange('remote_ip_prefix', e.target.value)}
                  style={styles.input}
                  placeholder={currentRule.ethertype === 'IPv4' ? '0.0.0.0/0' : '::/0'}
                  onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                  onBlur={(e) => e.target.style.borderColor = '#333'}
                />
              </div>
              
              {/* IP Version */}
              <div style={styles.dropdown} ref={ethertypeRef}>
                <label style={styles.label}>IP Version</label>
                <div
                  style={{
                    ...styles.dropdownTrigger,
                    borderColor: isEthertypeDropdownOpen ? '#e0eacc' : '#333'
                  }}
                  onClick={toggleEthertypeDropdown}
                >
                  <span>{currentRule.ethertype}</span>
                  <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                </div>
                {isEthertypeDropdownOpen && (
                  <div style={styles.dropdownMenu}>
                    {['IPv4', 'IPv6'].map(eth => (
                      <div
                        key={eth}
                        style={{
                          ...styles.dropdownItem,
                          backgroundColor: currentRule.ethertype === eth ? '#252525' : 'transparent'
                        }}
                        onClick={() => handleEthertypeSelect(eth)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#252525'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentRule.ethertype === eth ? '#252525' : 'transparent'}
                      >
                        {eth}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Port Range - Only for TCP/UDP */}
              {showPorts && (
                <>
                  <div>
                    <label style={styles.label}>Port Min</label>
                    <input
                      type="number"
                      value={currentRule.port_range_min}
                      onChange={(e) => handleRuleChange('port_range_min', e.target.value)}
                      min="1"
                      max="65535"
                      style={styles.input}
                      placeholder="1"
                      onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                      onBlur={(e) => e.target.style.borderColor = '#333'}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Port Max</label>
                    <input
                      type="number"
                      value={currentRule.port_range_max}
                      onChange={(e) => handleRuleChange('port_range_max', e.target.value)}
                      min="1"
                      max="65535"
                      style={styles.input}
                      placeholder="65535"
                      onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                      onBlur={(e) => e.target.style.borderColor = '#333'}
                    />
                  </div>
                </>
              )}
            </div>
            
            <button
              style={styles.addBtn(false)}
              onClick={addRule}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c8d9a9'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e0eacc'}
            >
              <FaPlus size={12} />
              Add Rule
            </button>
          </div>
          
          {/* RIGHT PANEL - Rules List */}
          <div style={styles.rightPanel}>
            <div style={styles.rulesHeader}>
              <h3 style={styles.rulesTitle}>RULES</h3>
              <span style={styles.rulesBadge(rules.length)}>{rules.length}</span>
            </div>
            
            {/* Stats Row */}
            {rules.length > 0 && (
              <div style={styles.statsRow}>
                <div style={styles.statBadge}>
                  <p style={styles.statValue('ingress')}>{ingressCount}</p>
                  <p style={styles.statLabel}>Ingress</p>
                </div>
                <div style={styles.statBadge}>
                  <p style={styles.statValue('egress')}>{egressCount}</p>
                  <p style={styles.statLabel}>Egress</p>
                </div>
              </div>
            )}
            
            {/* Rules List or Empty State */}
            {rules.length === 0 ? (
              <div style={styles.emptyRules}>
                <ShieldIcon size={36} color="#333" />
                <p style={styles.emptyTitle}>No rules added</p>
                <p style={styles.emptyText}>Add rules using the form</p>
              </div>
            ) : (
              <div style={styles.rulesList} className="fw-scrollbar">
                {rules.map((rule, index) => (
                  <div key={index} style={styles.ruleItem}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1 }}>
                      <span style={styles.ruleBadge(rule.direction)}>
                        {rule.direction === 'ingress' ? 'IN' : 'OUT'}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ color: 'white', fontSize: '11px', fontWeight: '500' }}>
                          {rule.protocol.toUpperCase()} {rule.port_range_min && rule.port_range_max ? `${rule.port_range_min}-${rule.port_range_max}` : 'All'}
                        </div>
                        <div style={{ color: '#555', fontSize: '9px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rule.remote_ip_prefix}
                        </div>
                      </div>
                    </div>
                    <button
                      style={styles.deleteBtn}
                      onClick={() => removeRule(index)}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)'; e.currentTarget.style.opacity = '1'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.opacity = '0.7'; }}
                    >
                      <FaTrash size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Scroll Hint */}
            {rules.length > 3 && (
              <div style={styles.scrollHint}>
                {rules.length} rules total
              </div>
            )}
            
            {/* Action Buttons */}
            <div style={styles.actionBtns}>
              <button
                style={styles.saveBtn(rules.length === 0)}
                disabled={rules.length === 0}
                onClick={handleSave}
                onMouseEnter={(e) => { if (rules.length > 0) e.currentTarget.style.backgroundColor = '#c8d9a9'; }}
                onMouseLeave={(e) => { if (rules.length > 0) e.currentTarget.style.backgroundColor = '#e0eacc'; }}
              >
                Save Rules
              </button>
              <button
                style={styles.cancelBtn}
                onClick={handleClose}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#444'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#333'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(FirewallModal);